<template>
  <div v-if="org">
    <v-toolbar Zdark elevation="0">
      <v-spacer />
    </v-toolbar>
    <v-container class="info-container grey lighten-5 py-0">
      <v-row>
        <v-col cols="12" sm="4">
          <h3>General Info</h3>
          <div class="label">ID</div>
          <div class="value"><pre>{{org.id}}</pre></div>
          
          <div class="label">Name</div>
          <div class="value">{{org.name}}</div>
          
          <div class="label">Contact</div>
          <div class="value">{{org.contact_email}} <v-btn icon color="accent" small @click="$helpers.copyToClipboard(org.email, $contact_email)"><v-icon small class="mr-2">fa-copy</v-icon></v-btn></div>

            
        </v-col>
        <v-col cols="12" sm="4">
          <h3>Subscription</h3>
          <div class="label">Plan</div>
          <div class="value">
            {{licenseInfo && licenseInfo.license | license }}
            <v-btn color="accent" outlined small class="ms-2" @click="showLicenseDialog=true">
              Apply
            </v-btn>
          </div>
          
          <div class="label">Purchased users</div>
          <div class="value">{{licenseInfo && licenseInfo.max_capacity}} (invoiced: {{ orgUtil.getMetaIntValue('invoiced.count') || '-' }})</div>
          
          <div class="label">Joined users</div>
          <div class="value">{{licenseInfo && licenseInfo.current_capacity}}</div>
          
          <div class="label">License valid?</div>
          <div class="value">{{ licenseInfo && licenseInfo.valid }}</div>
                    
        </v-col>
        <v-col cols="12" sm="4">
          <h3>Unassigned Products</h3>
          <ul v-if="licenseInfo && (licenseInfo.unassigned_users || licenseInfo.unassigned_support_plans)" class="mb-4">
            <template v-if="licenseInfo.unassigned_users">
              <li v-for="(key, idx) in Object.keys(licenseInfo.unassigned_users)" :key="idx">Plan: <strong>{{ key | license }} for {{licenseInfo.unassigned_users[key]}} users</strong></li>
            </template>
            <template v-if="licenseInfo.unassigned_support_plans">
              <li v-for="(key, idx) in Object.keys(licenseInfo.unassigned_support_plans)" :key="`${idx}_support`">Support plan: <strong>{{ key | supportPlan }} <span v-if="licenseInfo.unassigned_support_plans[key]>1">({{licenseInfo.unassigned_support_plans[key]}}x)</span></strong></li>
            </template>
          </ul>
          <p v-else class="text-muted">No unassigned products available.</p>
          <v-btn color="accent" outlined small class="ms-2" @click="showUnassignedDialog=true">
            Add
          </v-btn>
          
                    
        </v-col>
      </v-row>
    </v-container>

    <div class="pa-4">
      <h3>Events</h3>
      <EventGrid :items="events" class="mb-4" :routeResolver="resolveAdminRoute" />
      <p v-if="events && !events.length">No events created in this organization (yet).</p>

      <h3>Meta</h3>
      <v-data-table
        :headers="[ { text: 'Key', value: 'key' }, { text: 'Value (String)', value: 'val_str' }, { text: 'Value (Int)', value: 'val_int' }, { text: 'Value (Date)', value: 'val_dto' }, { text: 'Value (Double)', value: 'val_dbl' }  ]"
        :items="org.meta"
        :items-per-page="50"
        class="elevation-1 mb-8"
        />
      <h3>Logs</h3>
      <v-data-table
        v-if="logs"
        :headers="[ { text: 'Time', value: 'timestamp' }, { text: 'Level', value: 'level' }, { text: 'Tag', value: 'tag' }, { text: 'Message', value: 'msg' }, { text: 'By Admin?', value: 'admin' },  ]"
        :items="logs.data"
        :items-per-page="50"
        class="elevation-1"
        />
    </div>

    <multi-step-dialog v-model="showLicenseDialog" title="Apply License" confirm-label="Apply" single-step @confirm="applyLicense">
      <v-card-text>
        <v-switch v-model="licenseRequest.invoiced" label="Invoice created in MoneyBird" />

        <ToggleButtonInput
          v-model="licenseRequest.license"
          label="License"
          :items="[{type:null, text: 'None'}, {type:'BASIC', text: 'Starter'}, {type:'PRO', text: 'Pro'}, {type:'ENTERPRISE', text: 'Enterprise'}]"
          />

        <v-text-field v-model.number="licenseRequest.quantity" type="number" min="10" label="Number of participants" />

      </v-card-text>
    </multi-step-dialog>

    <multi-step-dialog v-model="showUnassignedDialog" title="Add Unassigned Products" confirm-label="Apply" single-step :can-confirm="Object.keys(unassignedProductsRequest).length > 0" @confirm="appendUnassignedLicenses">
      <v-card-text>
        <p class="d-flex flex-row">
          Users:
          <v-text-field v-model.number="selectedUsers" type="number" min="10" label="Number of participants" />
          License:
          <v-select v-model="selectedLicense"
            :items="['product-event-basic', 'product-event-pro', 'product-event-enterprise']"
            />
          <v-btn @click="unassignedProductsRequest[selectedLicense]=selectedUsers;$forceUpdate();">Add</v-btn>
        </p>
        <p class="d-flex flex-row">
          Support plan:
          <v-select v-model="selectedSupportPlan"
            :items="['product-setup-service', 'product-full-service']"
            />
          <v-btn @click="unassignedProductsRequest[selectedSupportPlan]=1;$forceUpdate();">Add</v-btn>
        </p>
        <p>
          <pre>{{ JSON.stringify(unassignedProductsRequest, null, 2) }}</pre>
        </p>

      </v-card-text>
    </multi-step-dialog>

  </div>
</template>

<script>
import store from "@/store";
import { mapGetters } from "vuex";
import adminService from "@/services/adminService";
import orgManagerService from "@/services/orgManagerService";
import { EventBus } from '@/plugins/eventbus.js';
import ToggleButtonInput from "@/components/ToggleButtonInput";
import MultiStepDialog from '@/components/generic/MultiStepDialog.vue';
import OrgUtil from "@/util/orgUtil";
import EventGrid from "@/components/EventGrid.vue";

export default {
  name: "AdminOrgDetails",
  components: {
    EventGrid,
    MultiStepDialog,
    ToggleButtonInput,
  },
  props: {
    org: Object,
    tenant: Object,
  },
  data() {
    return {
      error: null,
      events: null,
      logs: null,
      licenseInfo: null,
      showLicenseDialog: false,
      showUnassignedDialog: false,
      licenseRequest: {},
      unassignedProductsRequest: {},
      selectedSupportPlan: null,
      selectedLicense: null,
      selectedUsers: null,
    };
  },
  async mounted() {
    await this.loadData();
    
    EventBus.$on('login-state-change', async user => {
      await this.loadData();
    });
  },
  methods: {
    resolveAdminRoute(eventId) {
      return {name: 'adminEventDetails', params: {id: eventId}}
    },

    async loadData() {
      console.log('Loading event')
      if (this.user && this.org) {
        let id = this.$route.params.id;
        this.licenseInfo = (await orgManagerService.getLicenseStatus(id)).data;
        console.log('this.licenseInfo', this.licenseInfo);
        this.logs = (await orgManagerService.getLogs(id)).data;
        this.licenseRequest = {
          invoiced: this.orgUtil.hasMeta('invoiced.count'),
          license: this.licenseInfo.license,
          quantity: this.orgUtil.getMetaIntValue('invoiced.count') || 0,
        };
        this.events = (await orgManagerService.getEvents(this.org.id)).data.data;
      }
    },
    
    async applyLicense() {
      console.log('Apply license now', this.licenseRequest);
      var result = (await adminService.applyOrgLicense(this.org.id, this.licenseRequest.license, this.licenseRequest.quantity, this.licenseRequest.invoiced)).data;
      if (result.status == 'OK') {
        this.licenseRequest = {};
      }
      this.orgUtil.refresh();
      this.loadData();
    },

    async appendUnassignedLicenses() {
      console.log('Append license now', this.unassignedProductsRequest);
      var result = (await adminService.appendUnassignedOrgLicense(this.org.id, this.unassignedProductsRequest)).data;
      if (result.status == 'OK') {
        this.unassignedProductsRequest = {};
        this.selectedSupportPlan = null;
        this.selectedLicense = null;
        this.selectedUsers = null;
      }
      this.orgUtil.refresh();
      this.loadData();
    },
    

  },
  computed: {
    orgUtil() {
      return new OrgUtil(this, this.org);
    },
    ...mapGetters({
      user: "user"
    })
  },
  watch: {
    org() {
      if (this.licenseInfo == null) {
        this.loadData();
      }
    },
  }

};
</script>
<style lang="scss">

.search-box {
  max-width: 350px;
  float: right;
}
</style>

